import React from 'react';
import { Button as MuiButton, CircularProgress, styled } from '@mui/material';
import { Icon } from '../Icon';

export interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'danger' | 'outline';
  size?: 'small' | 'medium' | 'large';
  loading?: boolean;
  disabled?: boolean;
  icon?: string;
  className?: string;
  onClick?: () => void;
  children: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
}

const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== 'customVariant'
})<{ customVariant: 'primary' | 'secondary' | 'danger' | 'outline' }>(({ theme, customVariant }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '9999px',
  transition: 'all 0.2s ease-in-out',
  '&.Mui-disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  ...(customVariant === 'primary' && {
    backgroundColor: '#6150AD',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#5041a0',
    },
  }),
  ...(customVariant === 'secondary' && {
    backgroundColor: theme.palette.grey[500],
    color: '#ffffff',
    '&:hover': {
      backgroundColor: theme.palette.grey[600],
    },
  }),
  ...(customVariant === 'danger' && {
    backgroundColor: theme.palette.error.main,
    color: '#ffffff',
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  }),
  ...(customVariant === 'outline' && {
    backgroundColor: 'transparent',
    color: '#6150AD',
    border: '1px solid #6150AD',
    '&:hover': {
      backgroundColor: '#6150AD',
      color: '#ffffff',
    },
  }),
}));

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  loading = false,
  disabled = false,
  icon,
  className = '',
  onClick,
  children,
  type = 'button'
}) => {
  return (
    <StyledButton
      type={type}
      customVariant={variant}
      size={size}
      className={className}
      onClick={onClick}
      disabled={disabled || loading}
      startIcon={
        loading ? (
          <CircularProgress size={20} color="inherit" />
        ) : icon ? (
          <Icon name={icon as any} />
        ) : undefined
      }
    >
      {children}
    </StyledButton>
  );
};